import React, { useEffect, useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import httpHelper, { Method_TYPE } from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';

const PeriodsModal = ({ isModalOpen, setIsModalOpen, selectedPoint, fetchData, batchDetails }) => {

    useEffect(() => {
    if (selectedPoint) {
        const filteredBatches = batchDetails.filter(
            batch => batch.periodId === selectedPoint.id
        );

        setFormData({
            name: selectedPoint?.name,
            startDate: formatDate(selectedPoint?.start),
            endDate: formatDate(selectedPoint?.end),
            batches: filteredBatches.map(filter => ({
                name: filter?.name,
                startDate: filter?.startDate,
                endDate: filter?.endDate
            }))
        });
    }
}, [selectedPoint, batchDetails]);



const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;

};

    const [formData, setFormData] = useState({
        name: '',
        startDate: '',
        endDate: '',
        batches: [{ name: '', startDate: '', endDate: '' }]
    });

    const [editId, setEditId] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleCancel = () => {
        setIsModalOpen(false);
        setFormData({
            name: '',
            startDate: '',
            endDate: '',
            batches: [{ name: '', startDate: '', endDate: '' }]
        });
        setEditId('');
    };

    // Update period fields
    const handlePeriodChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Update batch fields
    const handleBatchChange = (index, field, value) => {
        const updatedBatches = [...formData.batches];
        updatedBatches[index][field] = value;
        setFormData(prev => ({
            ...prev,
            batches: updatedBatches
        }));
    };

    // Add batch
    const handleAddBatch = () => {
        setFormData(prev => ({
            ...prev,
            batches: [...prev.batches, { name: '', startDate: '', endDate: '' }]
        }));
    };

    // Remove last batch
    const handleRemoveLastBatch = () => {
        setFormData(prev => ({
            ...prev,
            batches: prev.batches.length > 1
                ? prev.batches.slice(0, -1)
                : prev.batches // keep at least one
        }));
    };

    const handleSubmit = async (e) => {
        console.log("Form Data Submitted:", formData);
        e.preventDefault();
        setIsLoading(true);
        try {
            if (selectedPoint) {
                await httpHelper(`${URL_CONSTANTS.EDIT_PERIODS}`, Method_TYPE.POST, formData);
            } else {
                await httpHelper(URL_CONSTANTS.PERIODS, Method_TYPE.POST, formData);
            }
            handleCancel();
            fetchData()
        } catch (error) {
            console.error('Error saving data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isModalOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <div>
                        <h2 className="font-heading font-semibold text-lg text-foreground">
                            {selectedPoint ? 'Edit Period' : 'Add Period'}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            Configure Periods requirements and details
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleCancel} iconName="X" />
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">

                    {/* Period Section */}
                    <div className="bg-primary/5 border-primary/20 bg-card border rounded-lg p-4 flex flex-col gap-3">
                        <label className='font-medium text-sm text-primary'>Period Information</label>
                        <Input
                            label="Name"
                            required
                            value={formData.name}
                            onChange={(e) => handlePeriodChange('name', e.target.value)}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                                type="date"
                                label="Start Date"
                                required
                                value={formData.startDate}
                                onChange={(e) => handlePeriodChange('startDate', e.target.value)}
                            />
                            <Input
                                type="date"
                                label="End Date"
                                required
                                value={formData.endDate}
                                onChange={(e) => handlePeriodChange('endDate', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Batches Section */}
                    <div className="bg-primary/5 border-primary/20 bg-card border rounded-lg p-4 flex flex-col gap-3">
                        <label className='font-medium text-sm text-primary'>Batch Information</label>

                        {formData.batches.map((batch, index) => (
                            <div
                                key={index}
                                className="relative border rounded-lg p-3 flex flex-col gap-3"
                            >
                                {/* Batch number badge */}
                                <span className="absolute top-2 right-2 bg-primary text-white text-xs font-bold px-2 py-1 rounded-full">
                                    Batch {index + 1}
                                </span>

                                <Input
                                    label="Name"
                                    required
                                    value={batch.name}
                                    onChange={(e) => handleBatchChange(index, 'name', e.target.value)}
                                />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input
                                        type="date"
                                        label="Start Date"
                                        required
                                        value={batch.startDate}
                                        onChange={(e) => handleBatchChange(index, 'startDate', e.target.value)}
                                    />
                                    <Input
                                        type="date"
                                        label="End Date"
                                        required
                                        value={batch.endDate}
                                        onChange={(e) => handleBatchChange(index, 'endDate', e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}

                        {/* Add/Remove Last Batch Buttons */}
                        <div className='flex justify-end gap-2'>
                            {formData.batches.length > 1 && (
                                <Button
                                    variant="danger"
                                    size="sm"
                                    iconName="Minus"
                                    type="button"
                                    onClick={handleRemoveLastBatch}
                                >
                                    Remove Batch
                                </Button>
                            )}
                            <Button
                                variant="success"
                                size="sm"
                                iconName="Plus"
                                type="button"
                                onClick={handleAddBatch}
                            >
                                Add Batch
                            </Button>
                        </div>
                    </div>


                    {/* Footer */}
                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
                        <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="default"
                            loading={isLoading}
                            iconName="Save"
                            iconPosition="left"
                        >
                            {selectedPoint ? 'Update Period' : 'Create Period'}
                        </Button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default PeriodsModal;
